import { useState } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';

interface ProfileRetryStepProps {
  /**
   * Inline error message from the last failed `updateMyProfile` call.
   * Shown in a destructive alert above the retry CTA.
   */
  readonly errorMessage: string;
  /**
   * Called when the user clicks "Retry saving your profile". The
   * parent re-runs `updateMyProfile` and on success advances out of
   * this view; on failure updates `errorMessage` and stays.
   */
  readonly onRetry: () => Promise<void>;
}

/**
 * Shown inside the sign-up dialog when `updateMyProfile` fails after
 * the user has already confirmed their email + been auto-signed-in.
 *
 * No nav escape (no "Sign in as someone else", no link to /signin) —
 * the user is half-onboarded (account exists, tokens in localStorage,
 * User record in DynamoDB lacking profile fields) and the only way
 * forward is to retry the profile save. Per the user's directive in
 * PR 19 review: "block navigating to /app unless the process was
 * successful".
 */
export function ProfileRetryStep({ errorMessage, onRetry }: ProfileRetryStepProps) {
  const [isRetrying, setIsRetrying] = useState(false);

  const retry = async () => {
    if (isRetrying) return;
    setIsRetrying(true);
    try {
      await onRetry();
    } finally {
      setIsRetrying(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <h3 className="text-foreground text-base font-semibold">Finish setting up your profile</h3>
        <p className="text-muted-foreground text-sm">
          Your account is created, but we couldn&apos;t save your profile details. Try again to
          continue.
        </p>
      </div>

      <Alert variant="destructive">
        <AlertDescription>{errorMessage}</AlertDescription>
      </Alert>

      <Button type="button" className="w-full" disabled={isRetrying} onClick={() => void retry()}>
        {isRetrying ? 'Saving…' : 'Retry saving your profile'}
      </Button>
    </div>
  );
}
